import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Badge,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Phone as PhoneIcon,
  FiberManualRecord as OnlineIcon,
  Schedule as AwayIcon,
  DoNotDisturb as BusyIcon,
  Restaurant as LunchIcon,
  Coffee as BreakIcon,
  MeetingRoom as MeetingIcon,
  RadioButtonUnchecked as OfflineIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import userPresenceService from '../../services/userPresence.service';

interface TeamMember {
  id: number;
  member_id: number;
  extension: string;
  custid: string;
  status: string;
  actual_status: string;
  custom_message?: string;
  last_activity: string;
  mem_fname: string;
  mem_lname: string;
  CompanyName: string;
  minutes_since_activity: number;
  department?: string;
  location?: string;
}

const statusConfig = {
  available: { icon: <OnlineIcon />, color: '#4caf50', label: 'Available' },
  away: { icon: <AwayIcon />, color: '#ff9800', label: 'Away' },
  busy: { icon: <BusyIcon />, color: '#f44336', label: 'Busy' },
  lunch: { icon: <LunchIcon />, color: '#ff5722', label: 'At Lunch' },
  break: { icon: <BreakIcon />, color: '#795548', label: 'On Break' },
  meeting: { icon: <MeetingIcon />, color: '#9c27b0', label: 'In Meeting' },
  offline: { icon: <OfflineIcon />, color: '#9e9e9e', label: 'Offline' }
};

const TeamPresence: React.FC = () => {
  const { member } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<TeamMember[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter options from API
  const [departments, setDepartments] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [extensionRanges, setExtensionRanges] = useState<any[]>([]);
  
  // Filters
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [extensionRange, setExtensionRange] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Status menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await userPresenceService.getTeamFilters();
      
      if (response.success) {
        setDepartments(response.data.departments);
        setLocations(response.data.locations);
        setExtensionRanges(response.data.extensionRanges);
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
    }
  }, []);

  const fetchPresence = useCallback(async () => {
    try {
      const response = await userPresenceService.getPresence();
      
      if (response.success) {
        setCurrentUser(response.data.currentUser);
        setTeamMembers(response.data.teamMembers);
        setFilteredMembers(response.data.teamMembers);
      }
    } catch (err) {
      console.error('Error fetching presence:', err);
      setError('Failed to load team presence');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateStatus = async (status: string, customMessage?: string) => {
    try {
      const response = await userPresenceService.updateStatus(status, customMessage);
      
      if (response.success) {
        fetchPresence();
        setStatusMenuAnchor(null);
      } else {
        setError('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Error updating status');
    }
  };

  const handleCall = (extension: string) => {
    // This will dial the user's extension first, then connect to the target extension
    // Implementation depends on your phone system API
    console.log(`Initiating call to extension ${extension}`);
    // TODO: Implement actual dialing logic
  };

  const applyFilters = useCallback(() => {
    let filtered = [...teamMembers];
    
    // Location filter
    if (locationFilter !== 'all') {
      filtered = filtered.filter(m => m.location === locationFilter);
    }
    
    // Department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(m => m.department === departmentFilter);
    }
    
    // Extension range filter
    if (extensionRange !== 'all') {
      const selectedRange = extensionRanges.find(r => r.value === extensionRange);
      if (selectedRange) {
        filtered = filtered.filter(m => {
          const ext = parseInt(m.extension);
          return ext >= selectedRange.min && ext <= selectedRange.max;
        });
      }
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => (m.actual_status || m.status) === statusFilter);
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(m => 
        m.mem_fname.toLowerCase().includes(search) ||
        m.mem_lname.toLowerCase().includes(search) ||
        m.extension.includes(search)
      );
    }
    
    setFilteredMembers(filtered);
  }, [teamMembers, locationFilter, departmentFilter, extensionRange, statusFilter, searchTerm]);

  useEffect(() => {
    fetchPresence();
    fetchFilters();
    const interval = setInterval(fetchPresence, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchPresence, fetchFilters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const renderMemberCard = (person: TeamMember) => {
    const status = person.actual_status || person.status;
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.offline;
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={`member-${person.member_id}-${person.extension}`}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" mb={1}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: config.color,
                      border: '2px solid white',
                    }}
                  />
                }
              >
                <Avatar sx={{ width: 36, height: 36, fontSize: '0.875rem' }}>
                  {person.mem_fname[0]}{person.mem_lname[0]}
                </Avatar>
              </Badge>
              <Box ml={1.5} flex={1}>
                <Typography variant="subtitle2" noWrap>
                  {person.mem_fname} {person.mem_lname}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Ext. {person.extension}
                </Typography>
              </Box>
              <Tooltip title="Call">
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => handleCall(person.extension)}
                  sx={{ ml: 'auto' }}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ color: config.color, display: 'flex' }}>
                {React.cloneElement(config.icon, { fontSize: 'small' })}
              </Box>
              <Typography variant="caption" sx={{ color: config.color }}>
                {config.label}
              </Typography>
            </Box>
            {person.custom_message && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block',
                  mt: 0.5,
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {person.custom_message}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filter Team Members</Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  label="Location"
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location} value={location}>{location}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  label="Department"
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Extension Range</InputLabel>
                <Select
                  value={extensionRange}
                  onChange={(e) => setExtensionRange(e.target.value)}
                  label="Extension Range"
                >
                  <MenuItem value="all">All Extensions</MenuItem>
                  {extensionRanges.map(range => (
                    <MenuItem key={range.value} value={range.value}>{range.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="away">Away</MenuItem>
                  <MenuItem value="busy">Busy</MenuItem>
                  <MenuItem value="lunch">At Lunch</MenuItem>
                  <MenuItem value="break">On Break</MenuItem>
                  <MenuItem value="meeting">In Meeting</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search by name or extension"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setLocationFilter('all');
                  setDepartmentFilter('all');
                  setExtensionRange('all');
                  setStatusFilter('all');
                  setSearchTerm('');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredMembers.length} of {teamMembers.length} team members
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Team Members Grid */}
      <Grid container spacing={2}>
        {filteredMembers.map(member => renderMemberCard(member))}
      </Grid>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {Object.entries(statusConfig).map(([statusKey, config]) => (
          <MenuItem key={statusKey} onClick={() => updateStatus(statusKey)}>
            <ListItemIcon sx={{ color: config.color }}>
              {config.icon}
            </ListItemIcon>
            <ListItemText>{config.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default TeamPresence;